'use client';

import React, { useEffect, type AnchorHTMLAttributes } from 'react';
import clsx from 'clsx';
import { format } from 'url';
import { usePathname } from 'next/navigation';
import type { LinkProps as NextLinkProps } from 'next/link';
import NextLink, { useLinkStatus } from 'next/link';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import { usePageTransitionDispatch } from '@packages/shared/src/providers/PageTransitionProvider/usePageTransition';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { getCanonicalUrl } from '@packages/shared/src/utils/getCanonicalUrl/getCanonicalUrl';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLocalizedPageUrlDispatch } from '@packages/shared/src/providers/LocalizedPageUrlProvider/useLocalizedPageUrl';
import { type Language } from '@packages/config';

export type LinkProps = NextLinkProps & {
  children?: React.ReactNode;
  className?: string;
  language?: Language;
} & React.RefAttributes<HTMLAnchorElement> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

const PageTransition = () => {
  const { pending } = useLinkStatus();
  const setLoading = usePageTransitionDispatch();

  useEffect(() => {
    setLoading(pending);
  }, [pending, setLoading]);

  return null;
};
export const Link = (
  props: {
    activeClassName?: string;
    openInNewTab?: boolean;
  } & LinkProps,
) => {
  const {
    activeClassName = 'active',
    className,
    href,
    openInNewTab,
    children,
    language,
    ref,
    prefetch = false,
    ...other
  } = props;
  const config = useConfig();
  const { language: actualLanguage } = useI18n();
  const pathname = usePathname();
  const setUrl = useLocalizedPageUrlDispatch();
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';
  const isBot = useIsBot();

  // it is possible that href is undefined, even though the type definitions do not allow it
  // e.g. when spreading props from a component that passes href conditionally
  // if the format function is called with undefined the whole application crashes
  const linkTarget = typeof href === 'undefined' ? '/' : href;
  let stringifiedHref = typeof linkTarget === 'string' ? linkTarget : format(linkTarget);

  if (isBot) {
    stringifiedHref = getCanonicalUrl(stringifiedHref);
  }

  const target = openInNewTab ? '_blank' : '_self';

  const classNames = clsx(className, {
    // startsWith instead of equality to avoid false negatives with query params, and work around trailing slash inconsistencies between server and client
    [activeClassName]: pathname && stringifiedHref.startsWith(pathname) && activeClassName,
  });

  // in cases where the link is intentionally for a different language, the `language` prop has to be used
  const targetLanguage = language ?? actualLanguage;
  // always add the correct language prefix to the href
  const localizedHref =
    // ignore some cases of hrefs for backwards compatibility
    stringifiedHref.startsWith('/')
      ? localizeUrl(stringifiedHref, targetLanguage, config)
      : stringifiedHref;

  // if we are in an appview, we can not render the next link. we need to use an default anchor so the redirect is not done via javascript
  if (isApp) {
    return (
      <a className={className} href={stringifiedHref} ref={ref} target={target} {...other}>
        {children}
      </a>
    );
  }

  return (
    <NextLink
      onNavigate={() => href && setUrl(href.toString())}
      data-testid="link"
      role="link"
      href={localizedHref}
      ref={ref}
      className={classNames}
      target={target}
      prefetch={prefetch}
      {...other}
    >
      <PageTransition />
      {children}
    </NextLink>
  );
};
